import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  IconButton,
  FormControl,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileDateTimePicker } from "@mui/x-date-pickers";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import dayjs from "dayjs";
import SendLocation from "./SendLocation";
import IndividualsAffectedField from "./IndividualsAffected";
import { shouldDisableDate } from "../../services/helper";
import { getFieldStyle, scrollToField } from "../fieldHelpers";
const HazardForm = ({
  formData,
  handleChange,
  individualsList,
  setIndividualsList,
  fileNames,
  setFileNames,
  highlightedField,
}) => {
  const [showLocationField, setShowLocationField] = useState(false);
  const [hazards, setHazards] = useState([]);
  // const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  // const pickerRef = useRef();
  const fieldRefs = {
    id: useRef(null),
    categoryType: useRef(null),
    description: useRef(null),
    enterLocation: useRef(null),
    sendLocation: useRef(null),
    reportedBy: useRef(null),
    otherReporter: useRef(null),
    dateOfObservation: useRef(null),
    attachedFile: useRef(null),
    individualsAffected: useRef(null),
    risk: useRef(null),
    priority: useRef(null),
    additionalComments: useRef(null),
  };

  useEffect(() => {
    scrollToField(highlightedField, fieldRefs); // Use the external helper
  }, [highlightedField]);

  useEffect(() => {
    const fetchBaseData = async () => {
      setLoading(true);
      const hazardsData = JSON.parse(localStorage.getItem("hazards")) || [];
      // const employeeData = JSON.parse(localStorage.getItem("reportedBy")) || [];
      setHazards(hazardsData ?? []);
      // setEmployees(employeeData ?? []);
      setLoading(false);
    };
    fetchBaseData();
  }, []);
  const setSelectedIndividual = useCallback(() => {}, []);

  const toggleLocationField = () => {
    setShowLocationField((prev) => !prev);
  };

  const uploadFile = (e) => {
    const file = e.target?.files[0];
    if (file) {
      let storedFile = new File([file], file.name, { type: file.type });
      setFileNames((prev) => [...prev, storedFile]);
      handleChange(e);
    }
  };

  const previewFile = (file) => {
    if (!file.url) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const fileContent = event.target.result;
        const blob = new Blob([fileContent], { type: file.type });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      };

      reader.onerror = (error) => {
        console.error("Error reading file:", error);
      };

      reader.readAsArrayBuffer(file);
    } else {
      window.open(file.url, "_blank");
    }
  };

  const removeFile = (fileRemove) => {
    setFileNames((prev) =>
      prev.filter((file) => file.name !== fileRemove.name),
    );
    handleChange({ target: { name: "removeFile", value: fileRemove } });
  };

  return (
    <>
      <Box>
        {!loading ? (
          <TextField
            fullWidth
            select
            margin="normal"
            name="categoryType"
            ref={fieldRefs["categoryType"]}
            sx={getFieldStyle("categoryType", highlightedField)}
            label="Select Hazard Type"
            value={formData.categoryType || ""}
            onChange={handleChange}
            required
          >
            {hazards.map((hazard) => (
              <MenuItem value={hazard.id} key={hazard.id}>
                {hazard.name}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Skeleton variant="rectangular" width={350} height={30} />
        )}
      </Box>

      {formData.categoryType === "other" && (
        <TextField
          fullWidth
          margin="normal"
          name="otherType"
          label="Specify Hazard Type"
          value={formData.otherType || ""}
          onChange={handleChange}
        />
      )}
      <TextField
        fullWidth
        margin="normal"
        name="description"
        ref={fieldRefs["description"]}
        sx={getFieldStyle("description", highlightedField)}
        label="Hazard Description"
        value={formData.description || ""}
        onChange={handleChange}
        required
      />

      <SendLocation
        ref={fieldRefs["sendLocation"]}
        sx={getFieldStyle("sendLocation", highlightedField)}
        formData={formData}
        handleChange={handleChange}
        highlighted={highlightedField === "sendLocation"}
      />
      {showLocationField && (
        <>
          <Typography align="center">OR</Typography>
          <TextField
            fullWidth
            margin="normal"
            name="enterLocation"
            ref={fieldRefs["enterLocation"]}
            sx={getFieldStyle("enterLocation", highlightedField)}
            label="Enter Location Manually"
            value={formData.enterLocation || ""}
            onChange={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={toggleLocationField}>
                    <LocationOnIcon /> {/* Same icon to close the field */}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </>
      )}

      <Box>
        {!loading ? (
          <TextField
            fullWidth
            margin="normal"
            name="reportedBy"
            ref={fieldRefs["reportedBy"]}
            sx={getFieldStyle("reportedBy", highlightedField)}
            label="Reported By"
            value={formData.reportedBy || ""}
            onChange={handleChange}
            required
          >
            {/* {employees.map((employee) => (
              <MenuItem value={employee.id} key={employee.id}>
                {employee.name}
              </MenuItem>
            ))} */}
          </TextField>
        ) : (
          <Skeleton variant="rectangular" width={350} height={30} />
        )}
      </Box>
      {formData.reportedBy === "other" && (
        <TextField
          fullWidth
          margin="normal"
          name="otherReporter"
          ref={fieldRefs["otherReporter"]}
          sx={getFieldStyle("otherReporter", highlightedField)}
          label="Other Reporter"
          value={formData.otherReporter || ""}
          onChange={handleChange}
        />
      )}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <FormControl
          ref={fieldRefs["dateOfObservation"]}
          fullWidth
          margin="normal"
          required
        >
          <Typography variant="body2" sx={{ color: "#888", marginTop: "10px" }}>
            Time & Date of Observation
          </Typography>
          <MobileDateTimePicker
            fullWidth
            name="dateOfObservation"
            sx={getFieldStyle("dateOfObservation", highlightedField)}
            shouldDisableDate={shouldDisableDate}
            value={
              formData.dateOfObservation
                ? dayjs(formData.dateOfObservation.$d)
                : null
            }
            onChange={(newValue) =>
              handleChange({
                target: { name: "dateOfObservation", value: newValue },
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{ marginTop: "2.5px" }}
                variant="outlined"
              />
            )}
            format="DD/MM/YY hh:mm A" // Includes AM/PM
          />
        </FormControl>
      </LocalizationProvider>
      <Button
        variant="outlined"
        component="label"
        sx={{
          display: "flex",
          alignItems: "center",
          marginTop: "16px",
          paddingTop: "16px",
          paddingBottom: "16px",
          marginBottom: "8px",
          ...getFieldStyle("attachedFile", highlightedField),
        }}
      >
        <AttachFileIcon sx={{ marginRight: "8px" }} />
        Add File
        <input
          type="file"
          hidden
          name="attachedFile"
          onChange={(e) => uploadFile(e)}
        />
      </Button>
      {/* Display Uploaded File Names with Remove Option */}
      <Box sx={{ marginTop: "10px" }}>
        {fileNames.map((file, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "5px 10px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              marginTop: "5px",
            }}
          >
            <Typography variant="body2">{file.name}</Typography>
            <Box>
              <Button
                color="primary"
                size="small"
                sx={{ marginRight: "8px" }}
                onClick={() => previewFile(file)}
              >
                Preview
              </Button>
              <Button
                color="error"
                size="small"
                onClick={() => removeFile(file)}
              >
                Remove
              </Button>
            </Box>
          </Box>
        ))}
      </Box>

      <IndividualsAffectedField
        formData={formData}
        handleChange={handleChange}
        individualsList={individualsList}
        setIndividualsList={setIndividualsList}
        setSelectedIndividual={setSelectedIndividual}
        ref={fieldRefs["individualsAffected"]}
        sx={getFieldStyle("individualsAffected", highlightedField)}
        highlighted={highlightedField === "individualsAffected"}
      />

      <Box>
        {formData.addedIndividuals &&
          formData.addedIndividuals.map((individual, index) => (
            <Typography key={index}>{individual}</Typography>
          ))}
      </Box>
      <TextField
        fullWidth
        select
        margin="normal"
        name="risk"
        label="Risk"
        ref={fieldRefs["risk"]}
        sx={getFieldStyle("risk", highlightedField)}
        value={formData.risk || ""}
        onChange={handleChange}
      >
        <MenuItem value="Low">1 - Low</MenuItem>
        <MenuItem value="Moderate">2 - Moderate</MenuItem>
        <MenuItem value="High">3 - High</MenuItem>
        <MenuItem value="Critical">4 - Critical</MenuItem>
      </TextField>
      <TextField
        fullWidth
        select
        margin="normal"
        name="priority"
        label="Priority"
        ref={fieldRefs["priority"]}
        sx={getFieldStyle("priority", highlightedField)}
        value={formData.priority || ""}
        onChange={handleChange}
      >
        <MenuItem value="Low">
          Low Priority - Address in routine schedules
        </MenuItem>
        <MenuItem value="Medium">Medium Priority - Address soon</MenuItem>
        <MenuItem value="High">
          High Priority - Immediate action required
        </MenuItem>
      </TextField>
      <FormControl
        fullWidth
        margin="normal"
        ref={fieldRefs["additionalComments"]}
        required
      >
        <Typography
          id="next-step-label"
          variant="body2"
          sx={{ color: "#888", marginTop: "10px" }}
        >
          Additional comments
        </Typography>
        <TextField
          id="additional-comments"
          name="additionalComments"
          value={formData.additionalComments || ""}
          onChange={handleChange}
          multiline
          rows={4} // You can adjust the number of rows to control the height of the textarea
          variant="outlined"
          placeholder="Enter your comments here..."
          sx={{
            marginTop: 1,
            ...getFieldStyle("additionalComments", highlightedField),
          }}
        />
      </FormControl>
    </>
  );
};

export default HazardForm;
